import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  SequestrationScenarioChart,
  SequestrationScenarioGauges,
  CombinedScenarioChart
} from "../charts/charts";
import "./SectionThree.css";
import InformationPopUp from "../tools/InformationPopUp";
import CarbonBalance from "./CarbonBalance";

export default function Escenarios() {
  const mapContainerClass = "map-container";
  const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { amount: 0.5, once: false });
  
    // helper for staggering
    const animateProps = (delay = 0) => ({
      initial: { opacity: 0, y: 10 },
      animate: isInView ? { opacity: 1, y: 0 } : {},
      transition: { duration: 0.8, delay }
    });
  

  return (
    <section
      ref={sectionRef}
      className="h-screen snap-start bg-gradient-to-b from-[#324130] to-[#92843B] text-white"
    >
      <div className="h-full p-6" style={{ marginTop: "20vh" }}>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-3xl font-bold mb-[-50] mt-10 text-center"
        >
          Emisiones de carbono
        </motion.h2>

        {/* Two-column layout */}
        <div className="w-full grid grid-cols-1 rounded-md lg:grid-cols-2" style={{ marginTop: "5vh" }}>

          {/* LEFT column: two line charts */}
          <div className="flex flex-col h-screen p-6" >
            {/* Centered heading over both columns */}
            <div className="absolute left-0 right-0 top-0 flex justify-center items-center" style={{ marginTop: "-8vh", zIndex: 10 }}>
              <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="text-xl font-bold"
                style={{ background: "rgba(0,0,0,0.25)", borderRadius: "0.5rem", padding: "0.75rem 2rem" }}
              >
                Emisiones de carbono por urbanización
              </motion.h2>
            </div>

            <motion.div {...animateProps(0.2)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl font-semibold mb-2"
              >
                Cambio en superficie de bosques y asentamientos por año
                <InformationPopUp
                  title="Cambio en superficie de bosques y asentamientos   por año"
                  description="En esta gráfica, se muestra el cambio histórico de la superficie cubierta por bosques (izquierda) y asentamientos (derecha) hasta el último año disponible (2022). Adicionalmente, se incluyen predicciones 20 años a futuro, correspondientes con tres escenarios de urbanización distintos:
            •	Inercial (negro): La tasa de urbanización sigue el mismo paso que las observaciones históricas.
            •	Acelerado (rojo): La urbanización se incrementa por un 15%.
            •	Desacelerado (verde): La urbanización se decrementa por 15%"
                  bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4 flex flex-row gap-4 items-start"style={{ height: "35vh", maxHeight: "35vh" }}>
                <CombinedScenarioChart />
              </div>
            </motion.div>
            <motion.div {...animateProps(0.6)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
                Balance de carbono
                <InformationPopUp title="Balance de carbono"
                description="En esta gráfica se muestra el balance de carbono debido a dos mecanismos: transiciones de uso de suelo, y carbono capturado por bosques. Un píxel azul indica que el neto fue negativo (i.e., absorbió carbono), mientras que un píxel rojo significa que se emitió. Las cantidades son totales acumulados desde 2000 hasta 2022."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                  source="Elaboración propia con base en SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className={`${mapContainerClass} relative w-full h-[40%] rounded-xl shadow-lg mt-5 `} style={{ height: "35vh", maxHeight: "35vh" }}>
                 <CarbonBalance />
               
              </div>
              
              
            </motion.div>
          </div>
          {/* New RIGHT column: emissions stacked bar chart */}

          <div className="flex flex-col h-screen p-6" >
            {/* Línea de emisiones por urbanización */}
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
              Potencial de captura de carbono por escenarios
              <InformationPopUp title="Potencial de captura de carbono por escenarios"
                description="En esta gráfica, se muestra el carbono capturado cada año, así como el potencial de captura a futuro para los tres escenarios de urbanización. Adicionalmente, se incluye la diferencia entre el escenario desacelerado y el acelerado, la cual representa el potencial de captura de carbono en un contexto sin medidas de conservación y reforestación frente a uno en el que sí se implementan dichas acciones."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                source="Elaboración propia con base en SISEPUEDE"
                fullCitation="https://sisepuede.readthedocs.io/"
              />
              </motion.h3>
              <div
                className="bg-black bg-opacity-30 rounded-lg p-4 flex flex-row gap-4 items-start"
                style={{ height: "35vh", maxHeight: "35vh" }}
              >
                <SequestrationScenarioChart />

              </div>
            </motion.div>

                        <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
              Potencial de captura de carbono por escenarios
              <InformationPopUp title="Potencial de captura de carbono por escenarios"
                description="En esta gráfica, se muestra el carbono capturado cada año, así como el potencial de captura a futuro para los tres escenarios de urbanización. Adicionalmente, se incluye la diferencia entre el escenario desacelerado y el acelerado, la cual representa el potencial de captura de carbono en un contexto sin medidas de conservación y reforestación frente a uno en el que sí se implementan dichas acciones."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                title_citation="GLC_FCS30D: Uso de Suelo a Nivel Global"
                source="Elaboración propia con base en SISEPUEDE"
                fullCitation="https://sisepuede.readthedocs.io/"
              />
              </motion.h3>
              <div className={` bg-black bg-opacity-30  rounded-md relative rounded-xl shadow-lg mt-5 `} style={{ height: "35vh", maxHeight: "35vh" }}>
                 <div style={{ flex: 1.2, height: "100%", overflow: "hidden" }}>
                  <SequestrationScenarioGauges />
                </div>
              </div>
            </motion.div>

             </div>
             
        </div>
      </div>
    </section>
  );
}